import React from 'react';
import { List, ListItem, ListItemText, Typography } from '@mui/material';
import { ModalType } from 'entities/modal/model';
import { ECOSYSTEM_LINKS, Icons, ModalSidebar } from 'shared';

export interface StakingModalPayload {
  data?: null;
  type: ModalType.Staking;
  isOpen?: boolean;
}

export const StakingModal = React.forwardRef<
  HTMLElement,
  Pick<StakingModalPayload, 'data' | 'isOpen'> & { onClose: VoidFunction }
>(({ onClose, isOpen }, ref) => {
  return (
    <ModalSidebar
      isOpen={isOpen}
      ref={ref}
      onClose={onClose}
      title="Staking Protocol"
      subtitle="• Secure the Network • Earn Rewards • Power the Ecosystem"
      icon={<Icons.StakingDark />}
      link={{
        title: 'Start Staking Today',
        link: {
          href: ECOSYSTEM_LINKS.staking,
          text: ECOSYSTEM_LINKS.staking,
          icon: '🔗',
        },
      }}
    >
      <Typography variant="inherit" color="inherit" fontFamily="inherit">
        The CratD2C Staking Protocol allows users to lock (stake) their $CRAT coins to secure the blockchain, validate
        transactions, and earn continuous rewards generated from block rewards, transaction fees, and EcoIP royalties.
      </Typography>
      <br />
      <Typography fontWeight="500" color="inherit" fontSize={18}>
        Roles Within the Staking Ecosystem
      </Typography>
      <br />
      <List
        disablePadding
        dense
        subheader={
          <>
            <Typography fontWeight="500" color="inherit" fontSize={18} mb={1.5}>
              🔗 Validators
            </Typography>
            <Typography fontWeight="inherit" color="inherit" fontSize="inherit">
              Advanced users or institutions who operate validator nodes.
            </Typography>
          </>
        }
      >
        <ListItem>
          <ListItemText secondary="• Secure the blockchain" />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Validate transactions" />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Earn lifetime rewards from block production, transaction fees, and EcoIP royalties." />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Receive delegations from the community." />
        </ListItem>
      </List>
      <br />
      <List
        disablePadding
        dense
        subheader={
          <>
            <Typography fontWeight="500" color="inherit" fontSize={18} mb={1.5}>
              🤝 Delegators
            </Typography>
            <Typography fontWeight="inherit" color="inherit" fontSize="inherit">
              Everyday users who stake $CRAT by assigning it to a trusted validator.
            </Typography>
          </>
        }
      >
        <ListItem>
          <ListItemText secondary="• No technical setup required" />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Earn passive staking rewards." />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Stay in full control of your $CRAT coins." />
        </ListItem>
      </List>
      <br />
      <List
        disablePadding
        dense
        subheader={
          <>
            <Typography fontWeight="500" color="inherit" fontSize={18} mb={1.5}>
              💎 LiteBackers
            </Typography>
            <Typography fontWeight="inherit" color="inherit" fontSize="inherit">
              Entry-level ecosystem supporters who stake moderate amounts.
            </Typography>
          </>
        }
      >
        <ListItem>
          <ListItemText secondary="• Earn staking rewards +" />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Receive Bonus EcoIP Portions (BIP)" />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Unlock modest lifetime royalties." />
        </ListItem>
      </List>
      <br />
      <List
        disablePadding
        dense
        subheader={
          <>
            <Typography fontWeight="500" color="inherit" fontSize={18} mb={1.5}>
              🚀 TurboBackers
            </Typography>
            <Typography fontWeight="inherit" color="inherit" fontSize="inherit">
              High-tier stakers who contribute larger amounts to secure the ecosystem.
            </Typography>
          </>
        }
      >
        <ListItem>
          <ListItemText secondary="• Earn higher staking rewards." />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Receive bigger EcoIP Portions" />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Unlock premium lifetime royalties tied to CratD2C’s intellectual property earnings." />
        </ListItem>
      </List>
      <br />
      <List
        disablePadding
        dense
        subheader={
          <Typography fontWeight="500" color="inherit" fontSize={18}>
            Why Stake on CratD2C?
          </Typography>
        }
      >
        <ListItem>
          <ListItemText secondary="• Earn consistent passive income." />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Strengthen network security and governance." />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Support validators and ecosystem growth." />
        </ListItem>
        <ListItem>
          <ListItemText secondary="• Unlock EcoIP Portion rewards (exclusive to CratD2C)" />
        </ListItem>
        <ListItem>
          <br />
          <ListItemText secondary="• Participate in building the world’s first D2C blockchain economy." />
        </ListItem>
      </List>
      <br />
      <Typography fontWeight="500" color="inherit" fontSize={18} mb={2}>
        Staking is Simple
      </Typography>
      <Typography variant="inherit" color="inherit" fontFamily="inherit">
        Whether you are staking as a Validator, Delegator, LiteBacker, or TurboBacker, your contribution powers the
        CratD2C SmartChain and earns you sustainable, long-term rewards.
      </Typography>
    </ModalSidebar>
  );
});
